import { setupNameChangeModal } from "./shared/name-change-modal";
import { ParticipantsList } from "./shared/participants";
import { startVisibilityTracker, type VisibilityTracker } from "./shared/visibility-tracker";
import { enableOpusStereo } from "./whep-sdp";
import { Urei1620Block } from "./main-vintage";

interface LiveUrlResponse {
  whepUrl: string;
  isLive: boolean;
}

interface ServerMessage {
  type:
    | "CHAT"
    | "PLAY_SE"
    | "SYSTEM"
    | "HEAT"
    | "USER_LIST"
    | "USER_JOIN"
    | "USER_LEAVE"
    | "USER_VISIBILITY";
  text?: string;
  seId?: string;
  user?: string;
  value?: number; // HEAT: 0〜100
  ts: number;
  // USER_LIST: unique name ごとに count + 集約 visible (= 新規接続者の initial snapshot)
  participants?: Array<{ name: string; count: number; visible: boolean }>;
  // USER_VISIBILITY / USER_JOIN: name 単位の集約 visible
  visible?: boolean;
}

let participantsList: ParticipantsList | null = null;
let visibilityTracker: VisibilityTracker | null = null;

// SE ファイル一覧 (public/se/ 配下に配置されたものをロード)
const SE_FILES = {
  horn: "/se/horn.mp3",
} as const;
type SEId = keyof typeof SE_FILES;

// ───────────── State ─────────────
const seBuffers = new Map<string, AudioBuffer>();
let videoEl: HTMLVideoElement | null = null;
let pc: RTCPeerConnection | null = null;
let whepUrl = "";
let audioCtx: AudioContext | null = null;
let mediaSource: MediaStreamAudioSourceNode | null = null;
// 5 バンド EQ: 60Hz lowshelf → 250Hz peak → 1kHz peak → 4kHz peak → 12kHz highshelf
let eqLow: BiquadFilterNode | null = null;
let eqLowMid: BiquadFilterNode | null = null;
let eqMid: BiquadFilterNode | null = null;
let eqHighMid: BiquadFilterNode | null = null;
let eqHigh: BiquadFilterNode | null = null;
let compressor: DynamicsCompressorNode | null = null;
// SE のマスターボリューム (configurable per-viewer)
let seGain: GainNode | null = null;
const SE_GAIN_DEFAULT = 0.15;

// ─── ルーティング (Vintage + Comp on/off) ───────────────────────────────
// 3 経路を **常時並列に動かして**、出力 GainNode で crossfade。
// disconnect/reconnect なし = クリックノイズなし。
//   compRouteGain   : mediaSource → compressor → ここ → EQ   (= Comp ON、Vintage OFF)
//   compBypassGain  : mediaSource → ここ → EQ                (= Comp OFF、Vintage OFF)
//   ureiRouteGain   : mediaSource → urei → ここ → EQ         (= Vintage ON、Comp 状態は無関係)
// Vintage ON が最優先 (= Comp 状態は伝わらない)。
let ureiBlock: Urei1620Block | null = null;
let compRouteGain: GainNode | null = null;
let compBypassGain: GainNode | null = null;
let ureiRouteGain: GainNode | null = null;
let vintageEnabled = true;
let vintageGlueEnabled = false;
let compEnabled = true;

// ─── Master volume + level meter ────────────────────────────────────────
// EQ の最終段の後ろに置く: eqHigh → masterGain → masterAnalyser → destination
// Analyser は setInterval(50ms) で peak を読み出す軽量メーター (rAF 非依存、tab 裏でも
// 続くが getFloatTimeDomainData + DOM 更新だけなので CPU 影響は無視できる程度)。
let masterGain: GainNode | null = null;
let masterAnalyser: AnalyserNode | null = null;
let masterMeterTimer: ReturnType<typeof setInterval> | null = null;
let masterMeterBuf: Float32Array<ArrayBuffer> | null = null;
let masterMeterPeakSmooth = 0;
// メーター表示の user preference。OFF にすると setInterval / DOM 更新を止めて
// CPU / バッテリーを節約する (= 配信中も「数字いらない」人向け)。
let masterMeterEnabled = true;
const MASTER_VOLUME_DEFAULT = 1.0;
let masterVolume01 = MASTER_VOLUME_DEFAULT;
// 接続切れの空白を埋めるブリッジノイズ
let bridgeSource: AudioBufferSourceNode | null = null;
let bridgeGain: GainNode | null = null;
// 生成済みノイズバッファを再利用 (短時間で連続切断 → 再接続のたびに 96k 要素の
// fill ループを走らせないため。Math.random() のコスト + 短期メモリスパイクを回避)
let bridgeBuffer: AudioBuffer | null = null;
let ws: WebSocket | null = null;
let userId = "";
// 招待 URL で来た場合、boot 時に decrypt した結果を保持しておき login submit 時に送る。
let inviteToken: string | null = null;

// 配信の再生状態。playing/paused は明示的なボタン操作で切り替わる。
//   idle       : 初回再生前 (AudioContext 未起動)
//   connecting : WHEP セッション確立中
//   playing    : PC 確立済み、音声/映像が流れている
//   paused    : ユーザーが ⏸ を押して PC を閉じている状態
type StreamState = "idle" | "connecting" | "playing" | "paused";
let streamState: StreamState = "idle";

// ───────────── Boot ─────────────
async function boot(): Promise<void> {
  const me = await fetchMe();
  if (me) {
    userId = me.name;
    document.body.classList.remove("locked");
    setMeName(me.name);
    await initApp();
  } else {
    setupLogin();
  }
}

function setupLogin(): void {
  const form = document.getElementById("login-form") as HTMLFormElement;
  const nameEl = document.getElementById("login-name") as HTMLInputElement;
  const aikotobaEl = document.getElementById("login-aikotoba") as HTMLInputElement;
  const submitBtn = document.getElementById("login-submit") as HTMLButtonElement;
  const errEl = document.getElementById("login-error")!;
  const inviteNote = document.getElementById("login-invite-note")!;

  // URL に ?invite=<token> があれば decrypt を試みて、aikotoba を prefill + readonly に
  void tryPrefillFromInvite(aikotobaEl, inviteNote, errEl);

  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    errEl.textContent = "";
    submitBtn.disabled = true;
    try {
      const res = await fetch("/api/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({
          name: nameEl.value.trim(),
          aikotoba: aikotobaEl.value,
          ...(inviteToken ? { invite: inviteToken } : {}),
        }),
      });
      if (res.status === 401) {
        errEl.textContent = "合言葉が違います";
        return;
      }
      if (!res.ok) {
        errEl.textContent = `エラー (${res.status})`;
        return;
      }
      const data = (await res.json()) as { name: string };
      userId = data.name;
      aikotobaEl.value = "";
      // 招待リンク経由でログインしたら URL から ?invite= を消す (再読み込み時のリプレイ防止)
      if (inviteToken) {
        const cleanUrl = new URL(location.href);
        cleanUrl.searchParams.delete("invite");
        history.replaceState(null, "", cleanUrl.toString());
        inviteToken = null;
      }
      document.body.classList.remove("locked");
      setMeName(data.name);
      await initApp();
    } catch {
      errEl.textContent = "通信エラー";
    } finally {
      submitBtn.disabled = false;
    }
  });
}

// ?invite= があれば decrypt して aikotoba 欄を prefill + readonly に。
// 失敗時はサイレントに通常 login にフォールバック。
async function tryPrefillFromInvite(
  aikotobaEl: HTMLInputElement,
  inviteNoteEl: HTMLElement,
  errEl: HTMLElement,
): Promise<void> {
  const token = new URL(location.href).searchParams.get("invite");
  if (!token) return;

  try {
    const res = await fetch("/api/invite-decrypt", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ invite: token }),
    });
    if (!res.ok) {
      if (res.status === 401) {
        errEl.textContent = "招待リンクが無効か期限切れです。合言葉を直接入力してください。";
      }
      return;
    }
    const data = (await res.json()) as { aikotoba: string; inviter: string };
    inviteToken = token;
    aikotobaEl.value = data.aikotoba;
    aikotobaEl.readOnly = true;
    aikotobaEl.classList.add("readonly");
    inviteNoteEl.textContent = `🎟️ ${data.inviter} さんからの招待で入室します。お名前だけ入れて「入室」を押してください。`;
    inviteNoteEl.hidden = false;
    // フォーカスを名前欄へ
    const nameEl = document.getElementById("login-name") as HTMLInputElement;
    nameEl.focus();
  } catch {
    // ネットワークエラー時は通常 login にそのままフォールバック
  }
}

async function fetchMe(): Promise<{ name: string } | null> {
  try {
    const res = await fetch("/api/me", {
      cache: "no-store",
      credentials: "same-origin",
    });
    if (!res.ok) return null;
    return (await res.json()) as { name: string };
  } catch {
    return null;
  }
}

function setMeName(name: string): void {
  const el = document.getElementById("me-name")!;
  el.textContent = `as ${name}`;
  const logoutBtn = document.getElementById("logout-btn") as HTMLButtonElement;
  logoutBtn.hidden = false;
  logoutBtn.addEventListener(
    "click",
    async () => {
      try {
        await fetch("/api/logout", { method: "POST", credentials: "same-origin" });
      } catch {
        // ignore
      }
      location.reload();
    },
    { once: true },
  );

  // 名前変更 popover (= ✏️ button click で開く、合言葉 + 新 name で再 login → reload)
  setupNameChangeModal(name);

  // 共有ボタン: 自分のパー券 (合言葉を含む暗号化トークン) を発行し、URL を共有
  const shareBtn = document.getElementById("share-btn") as HTMLButtonElement;
  shareBtn.hidden = false;
  shareBtn.addEventListener("click", handleShareClick);

  // /club への導線。ログイン済み (= session cookie あり) 状態でしか表示しない。
  // クリック後、cookie が同ドメインなので /club 側の `/api/me` が通って合言葉欄が出ない。
  const clubLink = document.getElementById("club-link") as HTMLElement | null;
  if (clubLink) clubLink.hidden = false;
  const liteLink = document.getElementById("lite-link") as HTMLElement | null;
  if (liteLink) liteLink.hidden = false;
}

async function handleShareClick(): Promise<void> {
  const btn = document.getElementById("share-btn") as HTMLButtonElement;
  btn.disabled = true;
  const originalText = btn.textContent;
  btn.textContent = "発行中…";

  try {
    const res = await fetch("/api/invite", {
      method: "POST",
      credentials: "same-origin",
    });
    if (!res.ok) {
      alert(`招待リンクを発行できませんでした (${res.status})`);
      return;
    }
    const data = (await res.json()) as { token: string; expiresAt: number };
    const inviteUrl = `${location.origin}/?invite=${encodeURIComponent(data.token)}`;
    const expiresDate = new Date(data.expiresAt * 1000);
    showShareModal(inviteUrl, expiresDate);
  } catch {
    alert("通信エラーで招待リンクを発行できませんでした");
  } finally {
    btn.disabled = false;
    btn.textContent = originalText;
  }
}

function showShareModal(url: string, expiresAt: Date): void {
  const modal = document.getElementById("share-modal")!;
  const urlEl = document.getElementById("share-url") as HTMLInputElement;
  const expEl = document.getElementById("share-expires")!;
  const copyBtn = document.getElementById("share-copy") as HTMLButtonElement;
  const closeBtn = document.getElementById("share-close") as HTMLButtonElement;

  urlEl.value = url;
  expEl.textContent = `有効期限: ${expiresAt.toLocaleString()}`;
  modal.hidden = false;

  copyBtn.textContent = "コピー";
  copyBtn.disabled = false;

  // onclick への代入で上書きするので、何度開いても古いリスナーは累積しない
  // (addEventListener + removeEventListener だと開閉のタイミングずれで残骸が出る恐れ)
  copyBtn.onclick = async () => {
    try {
      await navigator.clipboard.writeText(url);
      copyBtn.textContent = "✓ コピーしました";
    } catch {
      // clipboard API が使えない環境のフォールバック
      urlEl.select();
      copyBtn.textContent = "↑ URL を選択しました";
    }
  };
  closeBtn.onclick = () => {
    modal.hidden = true;
  };
}

async function initApp(): Promise<void> {
  videoEl = document.getElementById("player") as HTMLVideoElement;
  // 音は Web Audio 経由で出すので、video element 側は常時 mute
  videoEl.muted = true;
  videoEl.playsInline = true;
  videoEl.autoplay = true;
  videoEl.controls = false;

  const live = await fetchLiveUrl();
  if (live) {
    whepUrl = live.whepUrl;
    renderStreamStatus(live.isLive);
  } else {
    renderStreamStatusUnconfigured();
  }

  initChat();
  bindControls();

  document.getElementById("enable-audio")!.addEventListener("click", handlePlayPauseClick);

  // iOS Safari は WebRTC 音声のバックグラウンド再生に対応していないので
  // 画面に注意書きを出す。デスクトップ Safari / Chrome / Firefox は問題ないので非表示。
  if (isIOS()) {
    const note = document.getElementById("ios-note");
    if (note) note.hidden = false;
  }

  // タブが裏から戻ってきた瞬間、再生中だったのに PC が死んでいたら自動再接続する。
  // iOS Safari のアプリ切り替え/画面ロックで PC が壊れるケースを最小限のクリックで復帰させる。
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState !== "visible") return;
    if (streamState !== "playing") return;
    const dead =
      !pc ||
      pc.connectionState === "failed" ||
      pc.connectionState === "disconnected" ||
      pc.connectionState === "closed";
    if (dead) {
      void playStream();
    }
  });
}

function isIOS(): boolean {
  const ua = navigator.userAgent;
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  // iPadOS 13+ は Mac のフリをしてくるので touch があるかでも判定
  return ua.includes("Mac") && navigator.maxTouchPoints > 1;
}

// ───────────── API ─────────────
async function fetchLiveUrl(): Promise<LiveUrlResponse | null> {
  try {
    const res = await fetch("/api/get-live-url", { cache: "no-store" });
    if (!res.ok) return null;
    return (await res.json()) as LiveUrlResponse;
  } catch {
    return null;
  }
}

function renderStreamStatus(isLive: boolean): void {
  const el = document.getElementById("stream-status")!;
  el.textContent = isLive ? "🔴 LIVE" : "オフライン";
  el.classList.toggle("status--live", isLive);
  el.classList.toggle("status--offline", !isLive);
}

function renderStreamStatusUnconfigured(): void {
  const el = document.getElementById("stream-status")!;
  el.textContent = "Stream 未設定";
  el.classList.add("status--offline");
}

// ───────────── WHEP playback (play/pause) ─────────────
function setStreamState(next: StreamState): void {
  streamState = next;
  const btn = document.getElementById("enable-audio") as HTMLButtonElement;
  switch (next) {
    case "idle":
      btn.disabled = false;
      btn.textContent = "▶ 視聴を開始";
      break;
    case "connecting":
      btn.disabled = true;
      btn.textContent = "接続中…";
      break;
    case "playing":
      btn.disabled = false;
      btn.textContent = "⏸ 一時停止";
      break;
    case "paused":
      btn.disabled = false;
      btn.textContent = "▶ 再生";
      break;
  }
  updateMediaSessionPlaybackState();
}

// ───────────── Media Session (OS ロック画面 / コントロールセンター連携) ─────────────
// `navigator.mediaSession` で OS 側に「今この page でメディア再生してる」を通知する。
// ロック画面・コントロールセンター・Bluetooth ヘッドホンの ▶/⏸ がここを叩く。
//
// 制約:
//   - Chrome (desktop / Android) は Web Audio destination の出力を検知して controls を出す
//   - iOS Safari は `<audio>` / `<video>` 要素の audible 再生を要求する。`/` の video は
//     muted、audio は Web Audio destination 経由なので iOS では controls は出ない可能性が高い
//     (= 既知の制約、別アプローチが必要なら #ios-note を更新する)
let mediaSessionInitialized = false;
function setupMediaSession(): void {
  if (mediaSessionInitialized) return;
  if (!("mediaSession" in navigator)) return;
  mediaSessionInitialized = true;
  navigator.mediaSession.metadata = new MediaMetadata({
    title: "Pishio Live",
    artist: "DJ pishio",
  });
  // action handler は一度登録すれば streamState に関係なく OS が呼んでくる。
  // ハンドラ内で現在の state に応じて分岐する (= 重複再生 / 二重 pause 防止)。
  navigator.mediaSession.setActionHandler("play", () => {
    if (streamState !== "playing" && streamState !== "connecting") void playStream();
  });
  navigator.mediaSession.setActionHandler("pause", () => {
    if (streamState === "playing") pauseStream();
  });
  // 初期化前に走っていた setStreamState の playbackState は無視されているので、
  // ここで現在の state を OS に push する (= 接続中ずっと "none" になる問題を回避)。
  updateMediaSessionPlaybackState();
}

function updateMediaSessionPlaybackState(): void {
  if (!("mediaSession" in navigator) || !mediaSessionInitialized) return;
  navigator.mediaSession.playbackState =
    streamState === "playing" ? "playing"
    : streamState === "idle" ? "none"
    : "paused";
}

async function handlePlayPauseClick(): Promise<void> {
  if (streamState === "connecting") return;
  if (streamState === "playing") {
    pauseStream();
  } else {
    await playStream();
  }
}

async function playStream(): Promise<void> {
  setStreamState("connecting");

  // AudioContext は最初のユーザー操作の瞬間に resume すること (autoplay policy)
  if (!audioCtx) audioCtx = new AudioContext();
  if (audioCtx.state === "suspended") {
    try { await audioCtx.resume(); } catch { /* ignore */ }
  }

  // Media Session も最初の user gesture で初期化 (idempotent)。setActionHandler は
  // user activation を要求するブラウザがあるので、AudioContext.resume と同じ場所で。
  setupMediaSession();

  // 静的グラフ (5 バンド EQ + Compressor + SE Gain) は AudioContext の寿命中に 1 度だけ作る。
  // WHEP の MediaStreamSource はこのグラフの入り口 (eqLow) に毎回付け替える。
  if (!seGain) {
    initStaticAudioGraph();
    syncSliderValuesToNodes();
  }

  // SE プリロード: 未ロードの時だけ実行する。
  // decodeAudioData は main thread 上で重いので、再生/一時停止トグルや再接続で
  // 毎回走らせない。
  if (seBuffers.size === 0) {
    await preloadSEs();
  }

  if (!whepUrl) {
    // 配信 URL 未取得 (Stream 未設定など) でも SE と Web Audio は使える状態にする
    setStreamState("paused");
    return;
  }

  try {
    await connectWhep();
    setStreamState("playing");
  } catch (e) {
    console.warn("[WHEP] connect failed", e);
    const status = document.getElementById("stream-status")!;
    status.textContent = "配信に接続できませんでした (配信開始後に ▶ 再生 で再試行)";
    setStreamState("paused");
  }
}

function pauseStream(): void {
  // PC を close することで帯域も完全に切れる (= ローカルで mute するより honest)
  if (pc) {
    try { pc.close(); } catch { /* already closed */ }
    pc = null;
  }
  if (mediaSource) {
    try { mediaSource.disconnect(); } catch { /* already disconnected */ }
    mediaSource = null;
  }
  if (videoEl) {
    try { videoEl.pause(); } catch { /* ignore */ }
    videoEl.srcObject = null;
  }
  // 明示的な一時停止ではブリッジノイズも止める (= ユーザ意図の沈黙)
  stopBridgeNoise();
  // Master meter timer も止める (= paused 中の DOM 更新を停止して CPU 節約)
  stopMasterMeter();
  const status = document.getElementById("stream-status")!;
  status.textContent = "一時停止中";
  status.classList.remove("status--live");
  status.classList.add("status--offline");
  setStreamState("paused");
}

async function connectWhep(): Promise<void> {
  if (!videoEl || !audioCtx || !whepUrl) return;

  // 既に開いていれば閉じてから張り直す。mediaSource の disconnect は attachAudioSource 側で行う。
  if (pc) {
    try { pc.close(); } catch { /* ignore */ }
    pc = null;
  }

  pc = new RTCPeerConnection({ bundlePolicy: "max-bundle" });
  // 受信のみ (broadcaster は OBS → WHIP で別経路)
  pc.addTransceiver("audio", { direction: "recvonly" });
  pc.addTransceiver("video", { direction: "recvonly" });

  const stream = new MediaStream();
  pc.ontrack = (event) => {
    stream.addTrack(event.track);
    videoEl!.srcObject = stream;
    if (event.track.kind === "audio") {
      // audio が来たタイミングで MediaStreamSource を静的グラフの入り口に繋ぎ直す
      attachAudioSource(event.track);
    }
  };

  pc.onconnectionstatechange = () => {
    const state = pc?.connectionState;
    const el = document.getElementById("stream-status")!;
    if (state === "connected") {
      el.textContent = "🔴 LIVE";
      el.classList.add("status--live");
      el.classList.remove("status--offline");
      // 復帰したらブリッジノイズは fade out
      stopBridgeNoise();
    } else if (state === "disconnected") {
      // 一時的切断。ICE restart で自動復旧する余地があるので streamState は変えない。
      // 復旧までの空白を vinyl 風ノイズで埋める
      el.textContent = "接続不安定…";
      el.classList.remove("status--live");
      el.classList.add("status--offline");
      startBridgeNoise();
    } else if (state === "failed") {
      // 恒久的失敗。PC / mediaSource / video.srcObject を全部解放してから
      // ユーザに ▶ 再生 で再試行してもらう。
      // pauseStream() を呼ぶと status を「一時停止中」に上書きしてしまうので、
      // 後段で失敗用のメッセージに塗り直す。
      stopBridgeNoise();
      if (streamState === "playing" || streamState === "connecting") {
        pauseStream();
      }
      el.textContent = "接続失敗 (▶ で再試行)";
      el.classList.remove("status--live");
      el.classList.add("status--offline");
    }
  };

  const offer = await pc.createOffer();
  // Opus は WebRTC のデフォルトで mono にネゴシエートされる。offer の fmtp に
  // stereo=1;sprop-stereo=1 を明示して CloudFlare SFU が stereo decode/encode を
  // 維持するよう指示する。詳細は ./whep-sdp.ts を参照。
  offer.sdp = offer.sdp ? enableOpusStereo(offer.sdp) : offer.sdp;
  await pc.setLocalDescription(offer);
  await waitForIceGathering(pc);

  // body は **明示的に munge した offer.sdp** を使う (pc.localDescription.sdp は
  // 実装によって fmtp を再整形して stereo フラグを落とすことがある)。
  const res = await fetch(whepUrl, {
    method: "POST",
    headers: { "Content-Type": "application/sdp" },
    body: offer.sdp ?? pc.localDescription!.sdp,
  });
  if (!res.ok) {
    throw new Error(`WHEP POST failed: ${res.status}`);
  }
  // 防御的に answer 側にも同じ munging を掛ける (Cloudflare が応答 SDP で stereo
  // フラグを落とすパターンに備える。decoder 側がフラグを見て stereo 出力するため)。
  const answerSdp = enableOpusStereo(await res.text());
  await pc.setRemoteDescription({ type: "answer", sdp: answerSdp });

  try {
    await videoEl.play();
  } catch {
    // muted なので通常エラーにはならない
  }
}

function waitForIceGathering(pc: RTCPeerConnection): Promise<void> {
  if (pc.iceGatheringState === "complete") return Promise.resolve();
  return new Promise<void>((resolve) => {
    const safety = setTimeout(resolve, 3000); // 安全弁
    const handler = () => {
      if (pc.iceGatheringState === "complete") {
        clearTimeout(safety);
        pc.removeEventListener("icegatheringstatechange", handler);
        resolve();
      }
    };
    pc.addEventListener("icegatheringstatechange", handler);
  });
}

// ───────────── Audio graph ─────────────
// 静的グラフ (EQ + Compressor + SE Gain) を AudioContext の寿命中に 1 度だけ作る。
// 入り口の `eqLow` には後で `attachAudioSource()` から WHEP MediaStreamSource を繋ぐ。
function initStaticAudioGraph(): void {
  if (!audioCtx) return;

  // 5 バンド EQ
  eqLow = audioCtx.createBiquadFilter();
  eqLow.type = "lowshelf";
  eqLow.frequency.value = 60;
  eqLow.gain.value = 0;

  eqLowMid = audioCtx.createBiquadFilter();
  eqLowMid.type = "peaking";
  eqLowMid.frequency.value = 250;
  eqLowMid.Q.value = 1;
  eqLowMid.gain.value = 0;

  eqMid = audioCtx.createBiquadFilter();
  eqMid.type = "peaking";
  eqMid.frequency.value = 1000;
  eqMid.Q.value = 1;
  eqMid.gain.value = 0;

  eqHighMid = audioCtx.createBiquadFilter();
  eqHighMid.type = "peaking";
  eqHighMid.frequency.value = 4000;
  eqHighMid.Q.value = 1;
  eqHighMid.gain.value = 0;

  eqHigh = audioCtx.createBiquadFilter();
  eqHigh.type = "highshelf";
  eqHigh.frequency.value = 12000;
  eqHigh.gain.value = 0;

  compressor = audioCtx.createDynamicsCompressor();
  compressor.threshold.value = -24;
  compressor.knee.value = 30;
  compressor.ratio.value = 4;
  compressor.attack.value = 0.003;
  compressor.release.value = 0.25;

  seGain = audioCtx.createGain();
  seGain.gain.value = SE_GAIN_DEFAULT;

  // Vintage (UREI 1620) サブブロック + Comp bypass route。3 経路 (Comp 路 /
  // Comp bypass / UREI 路) を常時並列に動かし、出口の GainNode で crossfade。
  ureiBlock = new Urei1620Block(audioCtx);
  // 復元した glue preference を Urei block に反映 (= Vintage 起動時に最初から有効/無効が一致)
  if (vintageGlueEnabled) ureiBlock.setGlueEnabled(true);
  compRouteGain = audioCtx.createGain();
  compBypassGain = audioCtx.createGain();
  ureiRouteGain = audioCtx.createGain();
  // (vintageEnabled, compEnabled) から初期 gain を決める
  const v0 = vintageEnabled;
  const c0 = compEnabled;
  compRouteGain.gain.value = !v0 && c0 ? 1 : 0;
  compBypassGain.gain.value = !v0 && !c0 ? 1 : 0;
  ureiRouteGain.gain.value = v0 ? 1 : 0;

  // Master gain + level meter (= eqHigh の後、destination の前)
  masterGain = audioCtx.createGain();
  masterGain.gain.value = masterVolume01;
  masterAnalyser = audioCtx.createAnalyser();
  masterAnalyser.fftSize = 1024; // 1024 sample window = ~21ms @ 48kHz、軽量で peak 精度十分
  masterAnalyser.smoothingTimeConstant = 0; // peak は自前で decay 平滑する

  // 配信音声経路 (= EQ を最終段に置く順序、Comp 路 / Comp bypass / UREI 路を並列):
  //   (mediaSource) ─┬→ Compressor → compRouteGain ─┐
  //                  ├→ compBypassGain ──────────────┤→ EQ → masterGain → masterAnalyser → Destination
  //                  └→ Urei1620 → ureiRouteGain ────┘
  compressor.connect(compRouteGain);
  compRouteGain.connect(eqLow);
  compBypassGain.connect(eqLow);
  ureiBlock.output.connect(ureiRouteGain);
  ureiRouteGain.connect(eqLow);
  eqLow.connect(eqLowMid);
  eqLowMid.connect(eqMid);
  eqMid.connect(eqHighMid);
  eqHighMid.connect(eqHigh);
  eqHigh.connect(masterGain);
  masterGain.connect(masterAnalyser);
  masterAnalyser.connect(audioCtx.destination);

  // SE 経路: (AudioBufferSource) → seGain → Destination (EQ/Comp/Master バイパス)
  seGain.connect(audioCtx.destination);

  // Master meter は audio track が来た時 (= attachAudioSource) に起動し、pauseStream で
  // 止める。AudioContext 起動直後の connecting 段階では空回り (= -∞ dB を 20Hz で
  // DOM 更新) するだけなので、ここでは起動しない。
}

// すでにスライダーをいじっていた場合のために、現在値を input イベントで再発火させて
// ノードのパラメータに反映する。
function syncSliderValuesToNodes(): void {
  const ids = [
    "eq-low", "eq-low-mid", "eq-mid", "eq-high-mid", "eq-high",
    "comp-threshold", "comp-ratio", "comp-attack", "comp-release", "comp-knee",
    "se-volume", "master-volume",
  ];
  for (const id of ids) {
    document.getElementById(id)?.dispatchEvent(new Event("input"));
  }
}

// WHEP 再接続のたびに呼ばれる。以前の MediaStreamSource を切り離してから
// 新規 track を audio-only stream に包んで eqLow に流す。
function attachAudioSource(audioTrack: MediaStreamTrack): void {
  if (!audioCtx || !compressor || !ureiBlock) return;

  if (mediaSource) {
    try { mediaSource.disconnect(); } catch { /* already disconnected */ }
    mediaSource = null;
  }

  // video element 側は muted のため、音は Web Audio destination 経由のみで鳴る
  const audioOnly = new MediaStream([audioTrack]);
  mediaSource = audioCtx.createMediaStreamSource(audioOnly);
  // 3 経路すべてに同時送出 (= 出力側 GainNode で crossfade)。Comp ON/OFF と
  // Vintage ON/OFF の crossfade はすべて GainNode の ramp のみで処理されるので
  // disconnect / reconnect は不要。
  mediaSource.connect(compressor);
  if (compBypassGain) mediaSource.connect(compBypassGain);
  mediaSource.connect(ureiBlock.input);

  // ここで Master meter を起動/再開。pauseStream → playStream の resume 経路は
  // initStaticAudioGraph を再走しないので、track 接続のタイミングに紐付ける。
  startMasterMeter();
}

async function preloadSEs(): Promise<void> {
  if (!audioCtx) return;
  await Promise.all(
    (Object.entries(SE_FILES) as [SEId, string][]).map(async ([id, url]) => {
      try {
        const res = await fetch(url, { cache: "force-cache" });
        if (!res.ok) throw new Error(`status ${res.status}`);
        const arr = await res.arrayBuffer();
        const buf = await audioCtx!.decodeAudioData(arr);
        seBuffers.set(id, buf);
      } catch (e) {
        console.warn(`[SE] failed to load ${id}:`, e);
      }
    }),
  );
}

// SE は配信音声と独立した経路で再生する (EQ / Comp バイパス)。
// seGain → destination を経由するので、視聴者は SE 単独の音量を調整できる。
function playSE(seId: string): void {
  if (!audioCtx || !seGain) return;
  const buf = seBuffers.get(seId);
  if (!buf) return;
  const src = audioCtx.createBufferSource();
  src.buffer = buf;
  src.connect(seGain);
  src.start(0);
}

// ───────────── ルート切替 (Vintage / Comp on/off) ─────────────
// 3 経路 (compRouteGain / compBypassGain / ureiRouteGain) を (vintageEnabled,
// compEnabled) から決まる目標値に crossfade。disconnect / reconnect なし。
// Vintage ON が最優先 (= compEnabled を上書きする)。
const COMP_SLIDER_IDS = [
  "comp-threshold", "comp-ratio", "comp-attack", "comp-release", "comp-knee",
] as const;

function applyRoutes(timeConstant = 0.05): void {
  if (!audioCtx || !compRouteGain || !compBypassGain || !ureiRouteGain) return;
  const t = audioCtx.currentTime;
  const v = vintageEnabled;
  const c = compEnabled;
  // Safari は進行中の setTargetAtTime に新しい target を被せると click 音が出る。
  // 現在値を setValueAtTime で anchor してから新 target を流す (= main-vintage.ts
  // の rampGain と同パターン)。
  compRouteGain.gain.setValueAtTime(compRouteGain.gain.value, t);
  compRouteGain.gain.setTargetAtTime(!v && c ? 1 : 0, t, timeConstant);
  compBypassGain.gain.setValueAtTime(compBypassGain.gain.value, t);
  compBypassGain.gain.setTargetAtTime(!v && !c ? 1 : 0, t, timeConstant);
  ureiRouteGain.gain.setValueAtTime(ureiRouteGain.gain.value, t);
  ureiRouteGain.gain.setTargetAtTime(v ? 1 : 0, t, timeConstant);
}

// Vintage ON 時は comp checkbox + sliders を全部 disabled、OFF に戻ったら
// compEnabled に従って sliders を enable/disable する。
function syncCompControlsDisabled(): void {
  const compToggle = document.getElementById("comp-enabled") as HTMLInputElement | null;
  if (compToggle) compToggle.disabled = vintageEnabled;
  const slidersDisabled = vintageEnabled || !compEnabled;
  for (const id of COMP_SLIDER_IDS) {
    const el = document.getElementById(id) as HTMLInputElement | null;
    if (el) el.disabled = slidersDisabled;
  }
}

function setVintageEnabled(on: boolean): void {
  vintageEnabled = on;
  document.body.classList.toggle("vintage-on", on);
  applyRoutes();
  syncCompControlsDisabled();
  syncGlueToggleDisabled();
  try {
    localStorage.setItem("__mainVintageEnabled", on ? "1" : "0");
  } catch {
    // localStorage 不在環境 (= private mode 等) は無視
  }
}

function setVintageGlueEnabled(on: boolean): void {
  vintageGlueEnabled = on;
  if (ureiBlock) ureiBlock.setGlueEnabled(on);
  try {
    localStorage.setItem("__mainVintageGlueEnabled", on ? "1" : "0");
  } catch {
    // ignore
  }
}

// Vintage OFF の間は glue toggle を disabled に。OFF → ON で復帰時、
// glue の preference は localStorage 由来 (= 永続化済み) を尊重する。
function syncGlueToggleDisabled(): void {
  const el = document.getElementById("vintage-glue-toggle") as HTMLInputElement | null;
  if (el) el.disabled = !vintageEnabled;
}

function setCompEnabled(on: boolean): void {
  compEnabled = on;
  applyRoutes();
  syncCompControlsDisabled();
  try {
    localStorage.setItem("__mainCompEnabled", on ? "1" : "0");
  } catch {
    // localStorage 不在環境 (= private mode 等) は無視
  }
}

// ───────────── Master volume meter ─────────────
// setInterval(50ms) = 20fps の peak 検出。1024 sample buffer から abs(max) を取り、
// decay 平滑 (= 落ちる時はゆっくり) + dBFS 変換 + 色分け (緑→黄→赤) で表示。
// timer は **(stream playing AND user enabled)** の間だけ動く:
//   起動: attachAudioSource (= track 接続) + setMasterMeterEnabled(true)
//   停止: pauseStream + setMasterMeterEnabled(false)
// stream が paused / failed / idle の間は DOM 更新ゼロ、CPU 影響なし。
function startMasterMeter(): void {
  if (masterMeterTimer || !masterAnalyser) return;
  if (!masterMeterEnabled) return;
  // fftSize は init で 1024 固定。toggle / pause-resume の度に new 4096B しないよう、
  // 既存バッファを使い回す (= 連打時の GC churn 防止、Gemini 指摘対応)。
  if (!masterMeterBuf || masterMeterBuf.length !== masterAnalyser.fftSize) {
    masterMeterBuf = new Float32Array(masterAnalyser.fftSize) as Float32Array<ArrayBuffer>;
  }
  masterMeterTimer = setInterval(tickMasterMeter, 50);
}

function stopMasterMeter(): void {
  if (masterMeterTimer) {
    clearInterval(masterMeterTimer);
    masterMeterTimer = null;
  }
  masterMeterPeakSmooth = 0;
  resetMasterMeterUi();
}

/** メーター UI を「無信号」の見た目に戻す。fill=0% / 緑 / 値表記は
 *  enabled なら "-∞ dB"、disabled なら "--" にして「OFF にしてる」が一目でわかる形に。 */
function resetMasterMeterUi(): void {
  const fill = document.getElementById("master-meter-fill") as HTMLElement | null;
  const valEl = document.getElementById("master-meter-val") as HTMLElement | null;
  if (fill) {
    fill.style.width = "0%";
    fill.style.background = "hsl(120, 75%, 50%)";
  }
  if (valEl) valEl.textContent = masterMeterEnabled ? "-∞ dB" : "--";
}

function setMasterMeterEnabled(on: boolean): void {
  masterMeterEnabled = on;
  if (on) {
    // 現在 stream playing なら即起動、それ以外は UI だけ整える
    if (streamState === "playing") startMasterMeter();
    else resetMasterMeterUi();
  } else {
    stopMasterMeter();
  }
  try {
    localStorage.setItem("__mainMasterMeterEnabled", on ? "1" : "0");
  } catch {
    // localStorage 不在 (= private mode 等) は無視
  }
}

function tickMasterMeter(): void {
  if (!masterAnalyser || !masterMeterBuf) return;
  const fill = document.getElementById("master-meter-fill") as HTMLElement | null;
  const valEl = document.getElementById("master-meter-val") as HTMLElement | null;
  if (!fill || !valEl) return;
  masterAnalyser.getFloatTimeDomainData(masterMeterBuf);
  let peak = 0;
  for (let i = 0; i < masterMeterBuf.length; i++) {
    const a = Math.abs(masterMeterBuf[i]!);
    if (a > peak) peak = a;
  }
  // 上がる時は瞬間追従、下がる時は decay (= ~0.88 / 50ms ≒ -1 dB / 50ms、目に優しい)
  if (peak < masterMeterPeakSmooth) peak = Math.max(peak, masterMeterPeakSmooth * 0.88);
  masterMeterPeakSmooth = peak;
  const db = peak > 0 ? 20 * Math.log10(peak) : -Infinity;
  const clampedDb = Math.max(-60, Math.min(0, db === -Infinity ? -60 : db));
  // -60..0 dBFS を 0..100% にマッピング
  const widthPct = ((clampedDb + 60) / 60) * 100;
  fill.style.width = `${widthPct}%`;
  // 色: 緑 (<-12 dB) → 黄 (-12..-3) → 赤 (>-3)。clip 直前を一目で
  let hue: number;
  if (clampedDb > -3) hue = 0;
  else if (clampedDb > -12) hue = 45;
  else hue = 120;
  fill.style.background = `hsl(${hue}, 75%, 50%)`;
  valEl.textContent = db === -Infinity ? "-∞ dB" : `${db.toFixed(1)} dB`;
}

// ───────────── ブリッジノイズ (接続が切れた瞬間の繋ぎ音) ─────────────
// PeerConnection が disconnected になったら、視聴者の destination に
// レコード針のチリチリっぽい低レベルノイズを流して、DJ プレイの空白の
// 不快感を減らす。再接続したら fade out。
function startBridgeNoise(): void {
  if (!audioCtx) return;
  if (bridgeSource) return; // 既に鳴っている

  // 2 秒のホワイトノイズバッファ → 反復ループ + lowpass で vinyl っぽい質感。
  // 96k 要素の fill ループは初回だけ走らせて、以降は module-scoped にキャッシュ。
  // ネットワーク不安定で短期に何度も切断/復帰した時の CPU/メモリスパイクを潰す
  if (!bridgeBuffer) {
    const seconds = 2;
    bridgeBuffer = audioCtx.createBuffer(1, audioCtx.sampleRate * seconds, audioCtx.sampleRate);
    const data = bridgeBuffer.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1) * 0.5;
  }

  const src = audioCtx.createBufferSource();
  src.buffer = bridgeBuffer;
  src.loop = true;

  const filter = audioCtx.createBiquadFilter();
  filter.type = "lowpass";
  filter.frequency.value = 1500;

  const gain = audioCtx.createGain();
  gain.gain.value = 0;

  src.connect(filter);
  filter.connect(gain);
  gain.connect(audioCtx.destination);

  src.start(0);
  const t = audioCtx.currentTime;
  // linearRampToValueAtTime の前に setValueAtTime で開始値を明示しないと、
  // iOS Safari など一部実装で「いきなりジャンプする」「start が予測不能」になる
  gain.gain.setValueAtTime(0, t);
  gain.gain.linearRampToValueAtTime(0.18, t + 0.4);

  bridgeSource = src;
  bridgeGain = gain;
}

function stopBridgeNoise(): void {
  if (!audioCtx || !bridgeSource || !bridgeGain) return;
  const src = bridgeSource;
  const gain = bridgeGain;
  bridgeSource = null;
  bridgeGain = null;
  const t = audioCtx.currentTime;
  gain.gain.cancelScheduledValues(t);
  gain.gain.setValueAtTime(gain.gain.value, t);
  gain.gain.linearRampToValueAtTime(0, t + 0.3);
  setTimeout(() => {
    try { src.stop(); } catch { /* already stopped */ }
    try { src.disconnect(); } catch { /* ignore */ }
    try { gain.disconnect(); } catch { /* ignore */ }
  }, 400);
}

// ───────────── HEAT (DO からの定期 broadcast) ─────────────
function handleHeatMessage(value: number): void {
  const meter = document.getElementById("heat-meter");
  const fill = document.getElementById("heat-meter-fill");
  const valEl = document.getElementById("heat-meter-val");
  if (!meter || !fill || !valEl) return;
  meter.hidden = false;
  const clamped = Math.max(0, Math.min(100, value));
  fill.style.width = `${clamped}%`;
  // 青 (cool) → 赤 (hot) の hue 推移
  const hue = 220 - clamped * 2.2;
  fill.style.background = `hsl(${hue}, 85%, 55%)`;
  valEl.textContent = String(clamped);
}

// ───────────── Controls ─────────────
function bindControls(): void {
  const ramp = (param: AudioParam | null | undefined, value: number): void => {
    if (!param || !audioCtx) return;
    param.setTargetAtTime(value, audioCtx.currentTime, 0.02);
  };

  bindRange("eq-low", "eq-low-val", 1, (v) => ramp(eqLow?.gain, v));
  bindRange("eq-low-mid", "eq-low-mid-val", 1, (v) => ramp(eqLowMid?.gain, v));
  bindRange("eq-mid", "eq-mid-val", 1, (v) => ramp(eqMid?.gain, v));
  bindRange("eq-high-mid", "eq-high-mid-val", 1, (v) => ramp(eqHighMid?.gain, v));
  bindRange("eq-high", "eq-high-val", 1, (v) => ramp(eqHigh?.gain, v));
  bindRange("comp-threshold", "comp-threshold-val", 0, (v) => ramp(compressor?.threshold, v));
  bindRange("comp-ratio", "comp-ratio-val", 1, (v) => ramp(compressor?.ratio, v));
  bindRange("comp-attack", "comp-attack-val", 3, (v) => ramp(compressor?.attack, v));
  bindRange("comp-release", "comp-release-val", 3, (v) => ramp(compressor?.release, v));
  bindRange("comp-knee", "comp-knee-val", 0, (v) => ramp(compressor?.knee, v));
  // SE ボリューム: スライダーは 0〜100 (%) で表示、内部の GainNode は 0.0〜1.0 で持つ
  bindRange("se-volume", "se-volume-val", 0, (v) => ramp(seGain?.gain, v / 100));
  // Master ボリューム: 0〜150% (= -∞ ~ +3.5 dB)。配信ソースが小さい時の救済 + Vintage で
  // outputTrim 後にもう一段欲しい時のための headroom。
  bindRange("master-volume", "master-volume-val", 0, (v) => {
    masterVolume01 = Math.max(0, Math.min(1.5, v / 100));
    if (masterGain) ramp(masterGain.gain, masterVolume01);
    try { localStorage.setItem("__mainMasterVolume01", String(masterVolume01)); } catch { /* ignore */ }
  });

  document.getElementById("eq-reset")!.addEventListener("click", () => {
    const defaults: Record<string, number> = {
      "eq-low": 0,
      "eq-low-mid": 0,
      "eq-mid": 0,
      "eq-high-mid": 0,
      "eq-high": 0,
      "comp-threshold": -24,
      "comp-ratio": 4,
      "comp-attack": 0.003,
      "comp-release": 0.25,
      "comp-knee": 30,
      "se-volume": Math.round(SE_GAIN_DEFAULT * 100),
      "master-volume": Math.round(MASTER_VOLUME_DEFAULT * 100),
    };
    for (const [id, val] of Object.entries(defaults)) {
      const input = document.getElementById(id) as HTMLInputElement;
      input.value = String(val);
      input.dispatchEvent(new Event("input"));
    }
  });

  document.querySelectorAll<HTMLButtonElement>(".se-buttons button[data-se]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const seId = btn.dataset.se;
      if (!seId) return;
      if (ws?.readyState !== WebSocket.OPEN) return;
      ws.send(JSON.stringify({ type: "PLAY_SE", seId, user: userId }));
    });
  });

  // Vintage / Comp / Master の永続状態を localStorage から復元 (= 入室前から checkbox /
  // slider に visual 反映)。未設定なら Vintage=ON、Comp=ON、Master=100%、Meter=ON。
  try {
    const v = localStorage.getItem("__mainVintageEnabled");
    vintageEnabled = v === null ? true : v === "1";
    const c = localStorage.getItem("__mainCompEnabled");
    compEnabled = c === null ? true : c === "1";
    const m = localStorage.getItem("__mainMasterVolume01");
    if (m !== null) {
      const parsed = parseFloat(m);
      if (Number.isFinite(parsed)) masterVolume01 = Math.max(0, Math.min(1.5, parsed));
    }
    const me = localStorage.getItem("__mainMasterMeterEnabled");
    masterMeterEnabled = me === null ? true : me === "1";
    const vg = localStorage.getItem("__mainVintageGlueEnabled");
    vintageGlueEnabled = vg === "1"; // default OFF
  } catch {
    // private mode 等は default で進める
  }

  // Vintage toggle
  const vintageToggle = document.getElementById("vintage-toggle") as HTMLInputElement | null;
  if (vintageToggle) {
    vintageToggle.checked = vintageEnabled;
    vintageToggle.addEventListener("change", () => setVintageEnabled(vintageToggle.checked));
  }

  // Vintage Glue (Dynamic Glue) toggle — Vintage モード ON 時のみ有効化
  const glueToggle = document.getElementById("vintage-glue-toggle") as HTMLInputElement | null;
  if (glueToggle) {
    glueToggle.checked = vintageGlueEnabled;
    glueToggle.disabled = !vintageEnabled;
    glueToggle.addEventListener("change", () => setVintageGlueEnabled(glueToggle.checked));
  }

  // Comp on/off toggle
  const compToggle = document.getElementById("comp-enabled") as HTMLInputElement | null;
  if (compToggle) {
    compToggle.checked = compEnabled;
    compToggle.addEventListener("change", () => setCompEnabled(compToggle.checked));
  }

  // Master meter on/off toggle
  const meterToggle = document.getElementById("master-meter-enabled") as HTMLInputElement | null;
  if (meterToggle) {
    meterToggle.checked = masterMeterEnabled;
    meterToggle.addEventListener("change", () => setMasterMeterEnabled(meterToggle.checked));
    // 入室前 (= stream まだ playing でない) の段階で「OFF」復元なら readout を "--" に
    if (!masterMeterEnabled) resetMasterMeterUi();
  }

  // Master volume slider の DOM 値を復元値に揃える (= syncSliderValuesToNodes が
  // input イベントを発火させる時に、HTML default "100" ではなく復元値で
  // masterGain.gain が初期化されるようにする)
  const masterSlider = document.getElementById("master-volume") as HTMLInputElement | null;
  const masterOut = document.getElementById("master-volume-val") as HTMLOutputElement | null;
  if (masterSlider) {
    masterSlider.value = String(Math.round(masterVolume01 * 100));
    if (masterOut) masterOut.textContent = masterSlider.value;
  }

  // 再生開始前から正しい disabled state / body class を反映する
  document.body.classList.toggle("vintage-on", vintageEnabled);
  syncCompControlsDisabled();
}

function bindRange(
  id: string,
  outId: string,
  digits: number,
  onChange: (v: number) => void,
): void {
  const input = document.getElementById(id) as HTMLInputElement;
  const out = document.getElementById(outId) as HTMLOutputElement;
  const apply = () => {
    const v = parseFloat(input.value);
    out.textContent = v.toFixed(digits);
    onChange(v);
  };
  input.addEventListener("input", apply);
  apply();
}

// ───────────── Chat ─────────────
function initChat(): void {
  // 参加者リスト UI を chat-panel 内に bind (= HTML に <ul id="participants-list"> あり前提)
  const listEl = document.getElementById("participants-list") as HTMLElement | null;
  const countEl = document.getElementById("participants-count") as HTMLElement | null;
  if (listEl) participantsList = new ParticipantsList(listEl, countEl);

  connectWs();

  const form = document.getElementById("chat-form") as HTMLFormElement;
  const input = document.getElementById("chat-input") as HTMLInputElement;

  form.addEventListener("submit", (e) => {
    e.preventDefault();
    const text = input.value.trim();
    if (!text || ws?.readyState !== WebSocket.OPEN) return;
    ws.send(JSON.stringify({ type: "CHAT", text, user: userId }));
    input.value = "";
  });
}

// reconnect backoff state。open 成功で 5s にリセットされる。
let wsBackoffMs = 5000;
// visibility 復帰待ち中の listener (二重登録防止用)。
let wsVisibilityWaiter: (() => void) | null = null;

function connectWs(): void {
  // iOS Safari など、タブが裏のときに new WebSocket() を走らせると即座に
  // close → タイマーで再試行 → また close という無限ループになる。
  // visible に戻った瞬間に 1 度だけ再接続を試みる経路に分岐する。
  if (document.visibilityState === "hidden") {
    setWsStatus("切断 (バックグラウンド復帰待ち)");
    if (wsVisibilityWaiter) return;
    const onVisible = (): void => {
      if (document.visibilityState !== "visible") return;
      document.removeEventListener("visibilitychange", onVisible);
      wsVisibilityWaiter = null;
      connectWs();
    };
    wsVisibilityWaiter = onVisible;
    document.addEventListener("visibilitychange", onVisible);
    return;
  }

  const proto = location.protocol === "https:" ? "wss" : "ws";
  ws = new WebSocket(`${proto}://${location.host}/ws`);
  setWsStatus("接続中…");

  ws.addEventListener("open", () => {
    wsBackoffMs = 5000; // 成功したら backoff リセット
    setWsStatus("接続");
    // visibility tracker は 1 度だけ生成、WS 再接続のたびに現在状態を再送する
    if (!visibilityTracker) {
      visibilityTracker = startVisibilityTracker((msg) => {
        if (ws?.readyState === WebSocket.OPEN) ws.send(JSON.stringify(msg));
      });
    }
    visibilityTracker.notifyWsOpen();
  });
  ws.addEventListener("close", scheduleWsReconnect);
  ws.addEventListener("error", () => setWsStatus("エラー"));
  ws.addEventListener("message", (ev) => {
    let msg: ServerMessage;
    try {
      msg = JSON.parse(ev.data) as ServerMessage;
    } catch {
      return;
    }
    handleServerMessage(msg);
  });
}

// DO 再起動などで全 client が同時に切れた瞬間の thundering herd を避けるため、
// 5s ベースの backoff + 0〜2s jitter + 失敗ごとの 1.5x exponential (上限 30s)。
// 成功 (= open イベント) で 5s にリセットされる。
function scheduleWsReconnect(): void {
  const wait = wsBackoffMs + Math.floor(Math.random() * 2000);
  setWsStatus(`切断 (${Math.round(wait / 1000)}秒後に再接続)`);
  setTimeout(connectWs, wait);
  wsBackoffMs = Math.min(wsBackoffMs * 1.5, 30000);
}

function handleServerMessage(msg: ServerMessage): void {
  if (msg.type === "HEAT" && typeof msg.value === "number") {
    handleHeatMessage(msg.value);
    return;
  }
  if (msg.type === "PLAY_SE" && msg.seId) {
    playSE(msg.seId);
    return;
  }
  if (msg.type === "CHAT") {
    appendMessage("chat", `${msg.user ?? "anon"}: ${msg.text ?? ""}`);
    return;
  }
  if (msg.type === "SYSTEM") {
    appendMessage("system", msg.text ?? "");
    return;
  }
  // 参加者リスト系は共通モジュールに dispatch
  if (
    msg.type === "USER_LIST" ||
    msg.type === "USER_JOIN" ||
    msg.type === "USER_LEAVE" ||
    msg.type === "USER_VISIBILITY"
  ) {
    participantsList?.handleMessage(msg);
    return;
  }
}

function appendMessage(kind: "chat" | "system", text: string): void {
  const list = document.getElementById("messages")!;
  const el = document.createElement("div");
  el.className = `msg msg--${kind}`;
  el.textContent = text;
  list.appendChild(el);
  while (list.childElementCount > 200) list.firstElementChild?.remove();
  list.scrollTop = list.scrollHeight;
}

function setWsStatus(text: string): void {
  const el = document.getElementById("ws-status")!;
  el.textContent = `WS: ${text}`;
}

boot().catch((err) => {
  console.error("boot failed", err);
  const el = document.getElementById("stream-status");
  if (el) el.textContent = "起動エラー";
});
